import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Archive, ArrowLeft, Edit2, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@shadcn-in-nx/ui/input";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../../context/AuthContext";
import { usePaginationSearch } from "@utils";
import useStorage from "../../hooks/useStorage";
import useFavorites from "./hooks/useFavorites";
import MainLayout from "../../Layouts/main";
import Button from "../../components/Button.jsx";
import Icon from "../../components/Icon";
import Pagination from "../../components/Pagination";
import { DialogNewWorkflow } from "./DialogNewWorkflow";
import WorkflowToolbar from "./components/WorkflowToolbar";
import WorkflowListView from "./components/WorkflowListView";
import WorkflowGroupedView from "./components/WorkflowGroupedView";
import WorkflowBulkActions from "./components/WorkflowBulkActions";
import WorkflowListSkeleton from "./components/WorkflowListSkeleton";
import WorkflowCardView from "./components/WorkflowCardView";
import WorkflowPageHeader from "./components/WorkflowPageHeader";

export function Workflow() {
    const { t } = useTranslation();
    const { appId } = useParams();
    const navigate = useNavigate();
    const { interfaceStyle } = useAuth();
    const { favorites, toggleFavorite, isFavorite } = useFavorites();

    const [workflows, setWorkflows] = useState([]);
    const [groupedWorkflows, setGroupedWorkflows] = useState([]);
    const [currentApp, setCurrentApp] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [selectedWorkflows, setSelectedWorkflows] = useState(new Set());
    const [availableApps, setAvailableApps] = useState([]);
    const itemsPerPage = 30;

    const {
        currentPage, searchTerm, debouncedSearchTerm, showArchived,
        updatePage, updateSearch, updateShowArchived, clearSearch,
    } = usePaginationSearch();

    const [viewMode, setViewMode] = useStorage("viewMode", "list", window.localStorage);
    const [sortBy, setSortBy] = useStorage("workflowSort", "date_desc", window.localStorage);

    const prevRef = useRef({});

    const { call } = useBluelagoon({ method: "workflowsList" });
    const { call: callGrouped } = useBluelagoon({ method: "getGroupedWorkflows" });
    const { call: toggleArchive } = useBluelagoon({ method: "toggleArchiveWorkflow" });
    const { call: bulkArchive } = useBluelagoon({ method: "bulkArchiveWorkflows" });
    const { call: moveToApp } = useBluelagoon({ method: "moveWorkflowsToApp" });
    const { call: getApps } = useBluelagoon({ method: "appList" });
    const { call: updateAppCall } = useBluelagoon({ method: "appUpdate" });
    const { call: getApp } = useBluelagoon({ method: "app" });

    useEffect(() => {
        getApps({ limit: 100, status: "active" }).then((r) => {
            if (r?.apps) setAvailableApps(r.apps);
        });
    }, [getApps]);

    useEffect(() => {
        if (appId) {
            getApp({ id: appId }).then((r) => {
                if (r?.data) setCurrentApp(r.data);
            });
        } else {
            setCurrentApp(null);
        }
    }, [appId, getApp]);

    // Tracked as a ref (not derived from `initialLoading` state) so the
    // load callbacks below stay stable — keeping `initialLoading` in the deps
    // re-created the callback every time it flipped, churning identity for
    // any consumer downstream.
    const hasLoadedRef = useRef(false);

    const loadWorkflows = useCallback(
        async (page = 1) => {
            try {
                hasLoadedRef.current ? setLoading(true) : setInitialLoading(true);
                const response = await call({
                    limit: itemsPerPage,
                    offset: (page - 1) * itemsPerPage,
                    status: showArchived ? "ARCHIVED" : "ACTIVE",
                    search: debouncedSearchTerm || "",
                    appId: appId ?? "",
                });
                if (response?.workflows) {
                    setWorkflows(response.workflows);
                    setPagination(response.pagination);
                }
            } catch (err) {
                setError(t("workflowIndex.errors.loadFailed"));
            } finally {
                hasLoadedRef.current = true;
                setInitialLoading(false);
                setLoading(false);
            }
        },
        [call, itemsPerPage, showArchived, debouncedSearchTerm, t, appId]
    );

    const loadGroupedWorkflows = useCallback(
        async (page = 1) => {
            try {
                hasLoadedRef.current ? setLoading(true) : setInitialLoading(true);
                const response = await callGrouped({
                    limit: itemsPerPage,
                    offset: (page - 1) * itemsPerPage,
                    status: showArchived ? "ARCHIVED" : "ACTIVE",
                    search: debouncedSearchTerm || "",
                });
                if (response?.groups) {
                    setGroupedWorkflows(response.groups);
                    setPagination(response.pagination);
                }
            } catch (err) {
                setError(t("workflowIndex.errors.loadFailed"));
            } finally {
                hasLoadedRef.current = true;
                setInitialLoading(false);
                setLoading(false);
            }
        },
        [callGrouped, itemsPerPage, showArchived, debouncedSearchTerm, t]
    );

    useEffect(() => {
        const key = `${currentPage}-${showArchived}-${debouncedSearchTerm}-${viewMode}-${appId}`;
        if (prevRef.current.key === key) return;
        prevRef.current.key = key;
        if (appId || viewMode === "list" || viewMode === "cards") {
            loadWorkflows(currentPage);
        } else {
            loadGroupedWorkflows(currentPage);
        }
    }, [showArchived, currentPage, debouncedSearchTerm, viewMode, appId]);

    // Sort + favorites-first
    const sortedWorkflows = useMemo(() => {
        const sorted = [...workflows];
        sorted.sort((a, b) => {
            const aFav = favorites.has(a.id) ? 0 : 1;
            const bFav = favorites.has(b.id) ? 0 : 1;
            if (aFav !== bFav) return aFav - bFav;

            switch (sortBy) {
                case "name_asc": return a.name.localeCompare(b.name);
                case "name_desc": return b.name.localeCompare(a.name);
                case "date_asc": return new Date(a.createdAt) - new Date(b.createdAt);
                case "steps_desc": return b._count.steps - a._count.steps;
                case "executions_desc": return b._count.executions - a._count.executions;
                case "date_desc":
                default: return new Date(b.createdAt) - new Date(a.createdAt);
            }
        });
        return sorted;
    }, [workflows, sortBy, favorites]);

    const reload = (page = 1) => {
        updatePage(page);
        if (appId || viewMode === "list" || viewMode === "cards") loadWorkflows(page);
        else loadGroupedWorkflows(page);
    };

    const handleArchiveToggle = async (workflow) => {
        try {
            await toggleArchive({ workflowId: workflow.id });
            reload(1);
        } catch (err) {
            toast.error(t("workflowIndex.errors.updateFailed"));
        }
    };

    const handleBulkArchive = async (archive) => {
        if (selectedWorkflows.size === 0) return;
        try {
            await bulkArchive({ workflowIds: Array.from(selectedWorkflows), archive });
            setSelectedWorkflows(new Set());
            reload(1);
        } catch (err) {
            toast.error(t("workflowIndex.errors.bulkArchiveFailed"));
        }
    };

    const handleMoveToApp = async (targetAppId) => {
        if (selectedWorkflows.size === 0 || !targetAppId) return;
        try {
            await moveToApp({ workflowIds: Array.from(selectedWorkflows), targetAppId });
            setSelectedWorkflows(new Set());
            reload(1);
        } catch (err) {
            toast.error(t("workflowIndex.errors.moveFailed"));
        }
    };

    const handleSelectWorkflow = (id) => {
        const next = new Set(selectedWorkflows);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelectedWorkflows(next);
    };

    const handleSelectAll = () => {
        const allIds = isFlatView
            ? workflows.map((w) => w.id)
            : groupedWorkflows.flatMap((g) => g.workflows.map((w) => w.id));
        setSelectedWorkflows(
            selectedWorkflows.size === allIds.length ? new Set() : new Set(allIds)
        );
    };

    const handleSelectGroup = (groupWorkflows, checked) => {
        const next = new Set(selectedWorkflows);
        groupWorkflows.forEach((w) =>
            checked ? next.add(w.id) : next.delete(w.id)
        );
        setSelectedWorkflows(next);
    };

    const handleUpdateAppName = async (id, title) => {
        try {
            await updateAppCall({ appId: id, title });
            if (viewMode === "grouped") loadGroupedWorkflows(currentPage);
            if (appId && id === appId) {
                const r = await getApp({ id: appId });
                if (r?.data) setCurrentApp(r.data);
            }
        } catch (err) {
            toast.error(t("workflowIndex.errors.updateAppNameFailed"));
        }
    };

    const isFlatView = appId || viewMode === "list" || viewMode === "cards";

    const getBulkFlags = () => {
        const items = isFlatView
            ? workflows : groupedWorkflows.flatMap((g) => g.workflows);
        const selected = items.filter((w) => selectedWorkflows.has(w.id));
        return {
            showArchive: selected.some((w) => w.status === "ACTIVE"),
            showUnarchive: selected.some((w) => w.status === "ARCHIVED"),
        };
    };

    const totalCount = isFlatView
        ? workflows.length
        : groupedWorkflows.reduce((acc, g) => acc + g.workflows.length, 0);

    if (initialLoading) {
        return <MainLayout><WorkflowListSkeleton fullPage /></MainLayout>;
    }

    return (
        <MainLayout>
            <div className="space-y-4">
                <WorkflowPageHeader
                    appId={appId}
                    currentApp={currentApp}
                    workflows={workflows}
                    groupedWorkflows={groupedWorkflows}
                    viewMode={viewMode}
                    onUpdateAppName={handleUpdateAppName}
                />

                <WorkflowToolbar
                    searchTerm={searchTerm}
                    onSearchChange={updateSearch}
                    onClearSearch={clearSearch}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    hideViewToggle={!!appId}
                />

                <WorkflowBulkActions
                    selectedCount={selectedWorkflows.size}
                    {...getBulkFlags()}
                    availableApps={availableApps}
                    onArchive={() => handleBulkArchive(true)}
                    onUnarchive={() => handleBulkArchive(false)}
                    onMove={handleMoveToApp}
                    onClear={() => setSelectedWorkflows(new Set())}
                />

                <div className="flex items-center justify-between border-b pb-4">
                    <div className="flex items-center gap-1 sm:gap-2">
                        <Button
                            variant={showArchived ? "ghost" : "secondary"}
                            size="sm"
                            onClick={() => updateShowArchived(false)}
                            className="text-xs sm:text-sm"
                        >
                            {t("workflowIndex.tabs.active")}
                        </Button>
                        <Button
                            variant={showArchived ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => updateShowArchived(true)}
                            className="flex items-center gap-1 text-xs sm:text-sm"
                        >
                            <Archive className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="hidden sm:inline">
                                {t("workflowIndex.tabs.archived")}
                            </span>
                            <span className="sm:hidden">
                                {t("workflowIndex.tabs.archiveMobile")}
                            </span>
                        </Button>
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground">
                        {!loading && (
                            <>
                                {showArchived
                                    ? t("workflowIndex.status.archivedWorkflows", { count: totalCount })
                                    : t("workflowIndex.status.activeWorkflows", { count: totalCount })}
                                <span className="hidden sm:inline">
                                    {" "}{t("workflowIndex.status.workflows")}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="text-red-500 p-4 bg-red-50 rounded">{error}</div>
                )}

                {loading ? (
                    <WorkflowListSkeleton />
                ) : (
                    <div className="space-y-4">
                        {viewMode === "cards" && !appId ? (
                            <WorkflowCardView
                                workflows={sortedWorkflows}
                                searchTerm={debouncedSearchTerm}
                                showArchived={showArchived}
                                appId={appId}
                                currentApp={currentApp}
                            />
                        ) : (appId || viewMode === "list") ? (
                            <WorkflowListView
                                workflows={sortedWorkflows}
                                selectedWorkflows={selectedWorkflows}
                                onSelectAll={handleSelectAll}
                                onSelectWorkflow={handleSelectWorkflow}
                                onArchive={handleArchiveToggle}
                                onCopied={() => reload(1)}
                                isFavorite={isFavorite}
                                onToggleFavorite={toggleFavorite}
                                interfaceStyle={interfaceStyle}
                                searchTerm={debouncedSearchTerm}
                                showArchived={showArchived}
                                appId={appId}
                                currentApp={currentApp}
                            />
                        ) : (
                            <WorkflowGroupedView
                                groups={groupedWorkflows}
                                selectedWorkflows={selectedWorkflows}
                                onSelectGroup={handleSelectGroup}
                                onAppClick={(id) => navigate(`/workflows/apps/${id}`)}
                                onUpdateAppName={handleUpdateAppName}
                            />
                        )}
                        {pagination && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(pagination.total / itemsPerPage)}
                                onPageChange={updatePage}
                                itemsPerPage={itemsPerPage}
                                totalItems={pagination.total}
                                className="pt-4"
                            />
                        )}
                    </div>
                )}
            </div>
        </MainLayout>
    );
}
